import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, CardHeader, Divider, Stack, TextField, Typography, alpha } from '@mui/material';
import { useProject } from '../projects/ProjectContext';
import { getConfig } from '../config/bundlerConfig';
import { ethereumService } from '../utils/ethereum';
import { useAuth } from '@clerk/clerk-react';
import { apiClient } from '../services/apiClient';
import { toast } from 'react-toastify';

function LpLocker() {
  const { projectSettings } = useProject();
  const { getToken } = useAuth();
  const { currentProjectId } = useProject();
  const [tokenAddress, setTokenAddress] = useState('');
  const [lpAddress, setLpAddress] = useState('');
  const [lpLoading, setLpLoading] = useState(false);
  const [lpError, setLpError] = useState('');
  const [amountLp, setAmountLp] = useState('');
  const [unlockDateLocal, setUnlockDateLocal] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [lpBalance, setLpBalance] = useState(null);
  const [lpBalanceLoading, setLpBalanceLoading] = useState(false);
  const lpBalancePretty = useMemo(() => {
    const raw = (lpBalance?.balance || '').toString().trim();
    const n = Number(raw);
    if (!Number.isFinite(n)) return raw;
    // Show 4dp by default; keep tiny values readable.
    if (n === 0) return '0';
    if (Math.abs(n) < 0.0001) return n.toExponential(2);
    return n.toFixed(4);
  }, [lpBalance]);

  const runtimeConfig = useMemo(() => {
    try {
      return getConfig();
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    // Default unlock time: 50 years from now (local time for display).
    const d = new Date();
    d.setFullYear(d.getFullYear() + 50);
    const pad = (n) => String(n).padStart(2, '0');
    const local = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    setUnlockDateLocal(local);
  }, []);

  useEffect(() => {
    const addr = (projectSettings?.token_address || projectSettings?.tokenAddress || '').toString().trim();
    if (addr) setTokenAddress(addr);
  }, [projectSettings]);

  useEffect(() => {
    let cancelled = false;
    const autoDetectLp = async () => {
      if (!tokenAddress) return;
      setLpLoading(true);
      setLpError('');
      try {
        await ethereumService.ensureInitialized();
        const pair = await ethereumService.getUniswapV2PairAddress();
        if (!cancelled) {
          if (pair) {
            setLpAddress((prev) => (prev ? prev : pair));
          } else {
            setLpError('No liquidity pair detected yet for this token.');
          }
        }
      } catch (e) {
        if (!cancelled) {
          setLpError(e?.message || 'Could not auto-detect LP token. You can paste it manually.');
        }
      } finally {
        if (!cancelled) setLpLoading(false);
      }
    };
    autoDetectLp();
    return () => {
      cancelled = true;
    };
  }, [tokenAddress]);

  useEffect(() => {
    let cancelled = false;
    const loadLpBalance = async () => {
      if (!currentProjectId) return;
      if (!lpAddress) return;
      setLpBalanceLoading(true);
      try {
        const cfg = getConfig();
        const rpcUrl = (cfg?.network?.rpcUrl || '').toString().trim();
        if (!rpcUrl) throw new Error('RPC URL is not configured');
        const data = await apiClient({
          getToken,
          projectId: currentProjectId,
          path: '/api/me/launch/lp-balance',
          init: {
            method: 'POST',
            body: JSON.stringify({ rpcUrl, lpTokenAddress: lpAddress }),
          },
        });
        if (!cancelled) {
          setLpBalance({
            balance: data?.balance || '0',
            symbol: data?.lpToken?.symbol || 'LP',
            creationAddress: data?.creationWallet?.address || '',
          });
          // Auto-MAX by default (only if user hasn't already typed a value).
          if (!amountLp) {
            const v = (data?.balance || '').toString().trim();
            if (v) setAmountLp(v);
          }
        }
      } catch {
        if (!cancelled) setLpBalance(null);
      } finally {
        if (!cancelled) setLpBalanceLoading(false);
      }
    };
    loadLpBalance();
    return () => {
      cancelled = true;
    };
  }, [currentProjectId, getToken, lpAddress]);

  const lpHint = useMemo(() => {
    const router = (runtimeConfig?.network?.routerAddress || '').toString().trim();
    const dex = router ? 'your DEX (e.g. Uniswap V2 pair)' : 'your DEX (Uniswap / Sushi / etc.)';
    return `Paste the LP token address from ${dex}.`;
  }, [runtimeConfig]);

  const handleLockNow = async () => {
    if (!currentProjectId) return toast.error('Select a project first.');
    if (!lpAddress) return toast.error('LP token address is missing.');
    if (!amountLp || Number(amountLp) <= 0) return toast.error('Enter a valid LP amount.');
    if (!unlockDateLocal) return toast.error('Select an unlock date/time.');
    const ms = Date.parse(unlockDateLocal);
    if (!Number.isFinite(ms)) return toast.error('Unlock date/time is invalid.');

    setSubmitting(true);
    try {
      const cfg = getConfig();
      const rpcUrl = (cfg?.network?.rpcUrl || '').toString().trim();
      if (!rpcUrl) throw new Error('RPC URL is not configured (Admin → Platform settings)');

      const data = await apiClient({
        getToken,
        projectId: currentProjectId,
        path: '/api/me/launch/lock-liquidity',
        init: {
          method: 'POST',
          body: JSON.stringify({
            rpcUrl,
            lpTokenAddress: lpAddress,
            amountLp,
            unlockDate: new Date(ms).toISOString(),
            feeInEth: true,
            feeEth: '0.1',
            withdrawer: lpBalance?.creationAddress || null,
            referral: null,
          }),
        },
      });
      toast.success(data?.txHash ? `Lock submitted: ${String(data.txHash).slice(0, 12)}…` : 'Lock submitted');
    } catch (e) {
      toast.error(e?.message || 'Failed to lock liquidity');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box maxWidth="960px" mx="auto" mt={1.5}>
      <Card>
        <CardHeader
          title="LP Locker (UNCX Network)"
          subheader="Secure your project’s liquidity using the UNCX Network locking infrastructure."
        />
        <CardContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Review the detected LP pair and lock settings below. The lock transaction is submitted by your project’s
            <strong> creation wallet</strong>.
          </Typography>

          <Box
            sx={(t) => ({
              borderRadius: 3,
              border: `1px solid ${alpha(t.palette.primary.main, 0.18)}`,
              background: `linear-gradient(165deg, ${alpha(t.palette.primary.main, 0.06)} 0%, ${alpha('#000', 0.18)} 55%, ${alpha(t.palette.secondary.main, 0.04)} 100%)`,
              p: 2,
              mb: 2,
            })}
          >
            <Stack spacing={1}>
              <Typography variant="subtitle2" sx={{ fontWeight: 850, letterSpacing: 0.2 }}>
                Summary
              </Typography>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.25}
                sx={{ flexWrap: 'wrap' }}
                alignItems={{ xs: 'stretch', sm: 'center' }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 220 }}>
                  LP balance:{' '}
                  <Box component="span" sx={{ color: 'text.primary', fontWeight: 800 }}>
                    {lpBalanceLoading ? 'Loading…' : lpBalance?.balance ? `${lpBalancePretty} ${lpBalance.symbol}` : '—'}
                  </Box>
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 220 }}>
                  Fee:{' '}
                  <Box component="span" sx={{ color: 'text.primary', fontWeight: 800 }}>
                    0.1 ETH (fixed)
                  </Box>
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 220 }}>
                  Unlock:{' '}
                  <Box component="span" sx={{ color: 'text.primary', fontWeight: 800 }}>
                    50 years (fixed)
                  </Box>
                </Typography>
              </Stack>
              <Divider sx={{ borderColor: alpha('#fff', 0.08) }} />
              <Typography variant="caption" color="text.secondary">
                Owner / withdrawer:{' '}
                <Box component="span" sx={{ color: 'text.primary', fontWeight: 800, fontFamily: 'monospace' }}>
                  {lpBalance?.creationAddress ? `${lpBalance.creationAddress.slice(0, 8)}…${lpBalance.creationAddress.slice(-6)}` : '—'}
                </Box>
              </Typography>
            </Stack>
          </Box>

          <Stack spacing={2} mb={2}>
            <TextField
              label="Token address"
              placeholder="0x..."
              value={tokenAddress}
              onChange={(e) => setTokenAddress(e.target.value)}
              fullWidth
              helperText={
                projectSettings?.token_address || projectSettings?.tokenAddress
                  ? 'Auto-filled from this project’s assigned token.'
                  : 'Assign or deploy a token in Launch Wizard to auto-fill.'
              }
            />
            <TextField
              label="LP token address"
              placeholder="0x..."
              value={lpAddress}
              onChange={(e) => setLpAddress(e.target.value)}
              fullWidth
              helperText={
                lpError
                  ? lpError
                  : lpLoading
                  ? 'Detecting LP pair on-chain…'
                  : lpAddress
                  ? 'Auto-detected from on-chain pair. Verify it matches your DEX LP token.'
                  : lpHint
              }
            />
            <TextField
              label="LP amount to lock (MAX)"
              value={amountLp}
              fullWidth
              inputProps={{ inputMode: 'decimal' }}
              helperText="Locked to the full creation-wallet LP balance."
              disabled
            />
            <Box
              sx={{
                mt: -1.25,
                px: 0.25,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.25,
                flexWrap: 'wrap',
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {lpBalanceLoading
                  ? 'LP balance: loading…'
                  : lpBalance?.balance
                  ? `LP balance (creation wallet): ${lpBalancePretty} ${lpBalance.symbol}`
                  : 'LP balance (creation wallet): —'}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                disabled={!lpBalance?.balance || lpBalanceLoading}
                onClick={() => {
                  const v = (lpBalance?.balance || '').toString().trim();
                  if (!v) return;
                  setAmountLp(v);
                }}
                sx={{ borderRadius: 999, px: 1.25, py: 0.25, minWidth: 0 }}
              >
                MAX
              </Button>
            </Box>
            <TextField
              label="Unlock date/time (fixed)"
              type="datetime-local"
              value={unlockDateLocal}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="Fixed to 50 years from now."
              disabled
            />
            <TextField
              label="Owner / withdrawer (fixed)"
              value={lpBalance?.creationAddress || ''}
              fullWidth
              disabled
              helperText="Locked to the project creation wallet."
            />
            <TextField
              label="UNCX fee (ETH)"
              value="0.1"
              fullWidth
              disabled
              helperText="Fixed."
            />
          </Stack>

          <Alert severity="warning" sx={{ mb: 2 }}>
            Locking is final. Once locked, liquidity cannot be withdrawn until the unlock time.
          </Alert>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
            <Button variant="contained" color="primary" disabled={submitting || !amountLp || !lpAddress} onClick={handleLockNow}>
              {submitting ? 'Locking…' : 'Lock LP now'}
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ alignSelf: { xs: 'center', sm: 'center' } }}>
              Locks directly from this page using your project’s creation wallet.
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}

export default LpLocker;

